import { useState, useCallback, useEffect } from "react";
import {
  Joyride,
  type EventData,
  type Controls,
  type Step,
  STATUS,
  EVENTS,
} from "react-joyride";
import { useLocation } from "react-router-dom";
import { TOUR_STEPS } from "./tourSteps";

/* ------------------------------------------------------------------ */
/*  GuidedTour                                                         */
/* ------------------------------------------------------------------ */
/**
 * Behaviour (2026-05-15 / v3.0.68): tour is **on-demand only**.
 *
 * Pre-fix the component auto-started the tour the first time a user
 * landed on each page, gated by `sf-tour-completed` / `sf-tour-dismissed`
 * localStorage sets. Operators reported the spotlight popping up
 * mid-task unexpectedly and we don't want a 10-step popover hijacking
 * focus on a working accountant — the tour must be summoned, not
 * served.
 *
 * Now: nothing fires unless the parent passes `forceRun` (the "Start
 * Tour" button in the AppShell footer toggles it). We keep
 * `sf-tour-completed` for stats only — it tracks "this user has run
 * the tour on this page once" but no longer gates anything.
 */
const COMPLETED_KEY = "sf-tour-completed";

function loadSet(key: string): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(key) ?? "[]"));
  } catch {
    return new Set();
  }
}
function saveSet(key: string, s: Set<string>) {
  localStorage.setItem(key, JSON.stringify([...s]));
}

/* ------------------------------------------------------------------ */
/*  Filter steps whose target is absent from the DOM                   */
/* ------------------------------------------------------------------ */
function filterVisibleSteps(allSteps: Step[]): Step[] {
  return allSteps.filter((step) => {
    if (typeof step.target !== "string") return true;
    return step.target.split(",").some((sel) => {
      try { return !!document.querySelector(sel.trim()); }
      catch { return false; }
    });
  });
}

/* ------------------------------------------------------------------ */
/*  Resolve theme-aware colors at runtime                              */
/* ------------------------------------------------------------------ */
function resolveThemeColors() {
  const style = getComputedStyle(document.documentElement);
  const get = (v: string, fb: string) => style.getPropertyValue(v).trim() || fb;
  return {
    bgColor:      get("--c-bg-elevated", "#ffffff"),
    textColor:    get("--c-text-primary", "#1a1a2e"),
    primaryColor: get("--c-link-accent", "#6c5ce7"),
    arrowColor:   get("--c-bg-elevated", "#ffffff"),
    overlayColor: "rgba(0, 0, 0, 0.45)",
  };
}

type GuidedTourProps = {
  forceRun?: boolean;
  onForceRunConsumed?: () => void;
};

export function GuidedTour({ forceRun, onForceRunConsumed }: GuidedTourProps) {
  const { pathname } = useLocation();
  const allSteps = TOUR_STEPS[pathname];

  const [run, setRun] = useState(false);
  const [steps, setSteps] = useState<Step[]>([]);
  const [tourKey, setTourKey] = useState(0);
  const [colors, setColors] = useState(resolveThemeColors);

  // On-demand only: triggered exclusively by `forceRun`. No auto-start
  // on first visit, no localStorage gating. Operators summon the tour
  // explicitly via the "Start Tour" button in the sidebar footer.
  useEffect(() => {
    if (!forceRun || !allSteps) return;
    const timer = setTimeout(() => {
      const visible = filterVisibleSteps(allSteps);
      if (visible.length > 0) {
        setColors(resolveThemeColors());
        setSteps(visible);
        setTourKey((k) => k + 1);
        setRun(true);
      }
      onForceRunConsumed?.();
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceRun]);

  // Stop the tour when the user navigates away mid-flow.
  useEffect(() => {
    setRun(false);
  }, [pathname]);

  const handleEvent = useCallback(
    (data: EventData, _controls: Controls) => {
      const { status } = data;

      if (data.type === EVENTS.TARGET_NOT_FOUND) {
        setRun(false);
        return;
      }

      if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
        setRun(false);
        // Track completion for stats only — never gates auto-start
        // because there's no auto-start anymore.
        const s = loadSet(COMPLETED_KEY);
        s.add(pathname);
        saveSet(COMPLETED_KEY, s);
      }
    },
    [pathname],
  );

  if (!allSteps || steps.length === 0) return null;

  return (
    <Joyride
      key={tourKey}
      steps={steps}
      run={run}
      continuous
      scrollToFirstStep
      onEvent={handleEvent}
      locale={{
        back: "Back",
        close: "Close",
        last: "Finish",
        next: "Next",
        skip: "Skip tour",
      }}
      options={{
        overlayColor: colors.overlayColor,
        spotlightRadius: 8,
        zIndex: 1300,
        backgroundColor: colors.bgColor,
        textColor: colors.textColor,
        primaryColor: colors.primaryColor,
        arrowColor: colors.arrowColor,
      }}
    />
  );
}

/**
 * Clear local "I've seen this tour" history. Kept as a hook for
 * the sidebar footer button so an operator can replay any tour
 * without artifacts even though we don't gate on this set anymore.
 */
export function resetAllTours() {
  localStorage.removeItem(COMPLETED_KEY);
  // Legacy keys retained-for-removal so an upgrade from v3.0.67 or
  // earlier doesn't leave stale state in the browser.
  localStorage.removeItem("sf-tour-dismissed");
}
